import { useFileUpload } from '@hendrx/core/common/queries/fileUpload';
import { Box, Flex, Loader } from '@mantine/core';
import { Dropzone, FileRejection } from '@mantine/dropzone';
import cx from 'clsx';
import { CSSProperties, ComponentType, ReactElement, ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { humanFileSize } from 'shared/common/utils/core.utils';
import { showErrorNotification } from 'shared/utils/notifications';

import { DropzoneContent } from './DropzoneContent';
import { FileActions } from './FileActions';
import { FilePreview } from './FilePreview';
import { FileTypes } from './FileTypes';
import classes from './UploadFile.module.scss';

export type CustomClassType =
  | 'dropzoneIcon'
  | 'dropzoneContent'
  | 'dropzoneContentText'
  | 'dropzoneRoot'
  | 'errorMessage'
  | 'filePreview'
  | 'topFileActions';

export type CustomErrorType = 'fileTooLargeMessage' | 'fileInvalidTypeMessage' | 'tooManyFilesMessage';

export type UploadFileProps = {
  src?: string | null;
  alt?: string;
  Icon?: ComponentType;
  text: string;
  maxSize?: number;
  maxFiles?: number;
  accept: string[];
  onDrop: (src: string) => void;
  onDelete?: () => void;
  dropError: string;
  fileType?: FileTypes;
  previewMaxHeight?: number;
  aspectRatio?: string;
  isThumbnail?: boolean;
  previewObjectFit?: 'cover' | 'contain' | 'fill' | 'none';
  CustomResetButton?: ReactNode;
  disabled?: boolean;
  CustomDropzoneContent?: ReactElement;
  showFileActions?: boolean;
  fileActionsPosition?: 'flex-start' | 'center' | 'flex-end';
  customClassNames?: Partial<Record<CustomClassType, string>>;
  readOnly?: boolean;
  showOnlyLoaderWhenLoading?: boolean;
  customErrorMessages?: Partial<Record<CustomErrorType, string>>;
  dataTestId?: string;
};

export function UploadFile(props: UploadFileProps) {
  const {
    fileType = 'image',
    src,
    alt = '',
    maxSize = 1_000_000,
    maxFiles = 1,
    accept,
    text,
    Icon,
    onDrop,
    dropError,
    onDelete,
    previewMaxHeight,
    aspectRatio = 'auto',
    isThumbnail,
    previewObjectFit,
    CustomResetButton,
    disabled,
    CustomDropzoneContent,
    showFileActions = true,
    fileActionsPosition = 'center',
    customClassNames,
    readOnly,
    showOnlyLoaderWhenLoading,
    customErrorMessages,
    dataTestId
  } = props;

  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);
  const { mutateAsync: uploadFile, isPending } = useFileUpload();
  const uploadFileActionRef = useRef<() => void>(null);
  const [isDropped, setIsDropped] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { t } = useTranslation('components.uploadFileActions');
  const { t: utilsT } = useTranslation('components.uploadFileActions');

  const isFileReady = Boolean(src && !isPending);

  const isRejected = rejectedFiles.length !== 0;

  const style = useMemo(
    () =>
      ({
        '--upload-aspect-ratio': aspectRatio
      } as CSSProperties),
    [aspectRatio]
  );

  const handleUpload = async (uploadedFiles: File[]) => {
    try {
      setRejectedFiles([]);
      setErrorMessage('');
      setIsDropped(true);
      const src = await uploadFile(uploadedFiles[0]);
      if (!src) return;
      onDrop(src);
    } catch (e) {
      setErrorMessage(dropError);
    } finally {
      setIsDropped(false);
    }
  };

  const handleReject = (files: FileRejection[]) => {
    setRejectedFiles(files);
    let message = '';
    files.forEach(({ errors }) => {
      if (errors[0].code === 'file-too-large') {
        message =
          customErrorMessages?.fileTooLargeMessage ?? t('fileTooLargeMessage', { maxSize: humanFileSize(maxSize) });
      } else if (errors[0].code === 'file-invalid-type') {
        message = customErrorMessages?.fileInvalidTypeMessage ?? t('fileInvalidTypeMessage');
      } else if (errors[0].code === 'too-many-files') {
        message = customErrorMessages?.tooManyFilesMessage ?? t('tooManyFilesMessage', { count: maxFiles });
      } else {
        message = dropError;
      }
    });
    setErrorMessage(message);
    if (isFileReady) showErrorNotification(message, utilsT('notification.error'));
  };

  const handleReplace = () => {
    uploadFileActionRef?.current?.();
  };

  const handleDelete = () => {
    setRejectedFiles([]);
    setErrorMessage('');
    setIsDropped(false);
    onDelete?.();
  };

  return (
    <>
      {src && isFileReady && (
        <Box className={classes.fileWrapper}>
          <Flex gap={16} direction="column">
            <FilePreview
              previewMaxHeight={previewMaxHeight}
              aspectRatio={aspectRatio}
              type={fileType}
              alt={alt}
              src={src}
              disabled={disabled}
              isThumbnail={isThumbnail}
              customClassName={customClassNames?.filePreview}
              dataTestId={`${dataTestId ?? ''}-preview`}
              objectFit={previewObjectFit}
            />
            {!readOnly && showFileActions && (
              <Box
                className={cx(classes.fileActions, isThumbnail && classes.thumbnail, customClassNames?.topFileActions)}
              >
                <FileActions
                  onDelete={onDelete && handleDelete}
                  onReplace={handleReplace}
                  CustomResetButton={CustomResetButton}
                  disabled={disabled}
                  justify={fileActionsPosition}
                />
              </Box>
            )}
          </Flex>
        </Box>
      )}
      <Dropzone
        style={{ display: isFileReady ? 'none' : 'block', ...style }}
        openRef={uploadFileActionRef}
        onDrop={handleUpload}
        onReject={handleReject}
        maxSize={maxSize}
        maxFiles={maxFiles}
        accept={accept}
        classNames={{
          root: cx(
            classes.dropzone,
            isRejected && classes.rejected,
            disabled && classes.disabled,
            customClassNames?.dropzoneRoot
          ),
          inner: classes.dropzoneInner
        }}
        disabled={disabled || readOnly}
        data-testid={`${dataTestId ?? ''}-upload`}
      >
        <Flex justify="center" align="center" direction="column" h="100%">
          {rejectedFiles.length > 0 || errorMessage ? (
            <DropzoneContent
              Icon={Icon}
              isRejected={rejectedFiles.length !== 0}
              text={errorMessage}
              isThumbnail={isThumbnail}
              CustomDropzoneContent={CustomDropzoneContent}
              customClassNames={customClassNames}
            />
          ) : (
            <DropzoneContent
              Icon={isDropped ? () => <Loader data-testid="upload-file-loader" /> : Icon}
              text={text}
              isRejected={rejectedFiles.length !== 0}
              subText={`${t('maxSize', { maxSize: humanFileSize(maxSize) })}`}
              isThumbnail={isThumbnail}
              CustomDropzoneContent={CustomDropzoneContent}
              customClassNames={customClassNames}
              showOnlyLoaderWhenLoading={isDropped && showOnlyLoaderWhenLoading}
            />
          )}
        </Flex>
      </Dropzone>
    </>
  );
}
